import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TutorReportsHelperService } from '../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { SearchPlacementRequest } from '../../_models/placement-search/search-placement-request.model';
import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { SearchPlacementResult } from '../../_models/placement-search/search-placement-result.model';
import { PlacementService } from '../../_services';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { PlacementSession } from '../../_models/tutor-reports/placement-session.model';
import { StudentBooking } from '../../_models/student-bookings/student-booking';
import { PlacementData } from '../../_models';
import { PlacementMaintenance } from '../../_models/maintenance/placement-maintenance.model';
import { animate, group, query, style, transition, trigger } from '@angular/animations';

import { SelectedSessionDate } from '../../_models/student-bookings/selected-session-date.model';

const left = [
  query(':enter, :leave', style({ position: 'fixed', width: '100%' }), { optional: true }),
  group([
    query(':enter', [style({ transform: 'translateX(-100%)' }), animate('.3s ease-out', style({ transform: 'translateX(0%)' }))], {
      optional: true,
    }),
    query(':leave', [style({ transform: 'translateX(0%)' }), animate('.3s ease-out', style({ transform: 'translateX(100%)' }))], {
      optional: true,
    }),
  ]),
];

const right = [
  query(':enter, :leave', style({ position: 'fixed', width: '100%' }), { optional: true }),
  group([
    query(':enter', [style({ transform: 'translateX(100%)' }), animate('.3s ease-out', style({ transform: 'translateX(0%)' }))], {
      optional: true,
    }),
    query(':leave', [style({ transform: 'translateX(0%)' }), animate('.3s ease-out', style({ transform: 'translateX(-100%)' }))], {
      optional: true,
    }),
  ]),
];

@Component({
    selector: 'view-student-bookings',
    templateUrl: './view-student-bookings.component.html',
    styleUrls: ['./view-student-bookings.component.css'],
    animations: [
        trigger('slider', [
            transition(':increment', right),
            transition(':decrement', left),
        ]),
    ],
    standalone: false
})
export class ViewStudentBookingsComponent implements OnInit, OnDestroy {
  @ViewChild('sessionCalendar', { static: false }) sessionCalendar: any;
  @ViewChild('utilityBarForm', { static: true }) utilityBarForm: TemplateRef<any>;

  public placementData: PlacementData;
  public placementMaintenance: PlacementMaintenance;
  public searchPlacementResult: SearchPlacementResult;
  public studentBooking: StudentBooking;
  public showViewSessionPanel: boolean = false;
  public placementId: number;
  public selectedSessionInfo: SelectedSessionDate;
  public lessonType: string;

  public panelNumber: 1 | 2 = 1;

  /** The status legend doubles as a filter. `activeStatuses` empty = show all; otherwise only the
   * selected statuses render in the calendar (passed down as its `statusFilter` input). */
  public readonly sessionStatuses: string[] = ['New', 'Saved', 'Submitted', 'Approved', 'Rejected', 'Cancelled'];
  public activeStatuses: string[] = [];

  private selectedSessionHolder: PlacementSession;
  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public themeService: ThemeService,
    private router: Router,
    private route: ActivatedRoute,
    private tutorReportsHelperService: TutorReportsHelperService,
    private placementService: PlacementService,
    private errorModalHelper: ErrorModalHelperService,
    private featureHelperService: FeatureHelperService
  ) {
    let featureList = new Array<string>();
    featureList.push("TutorReportsView");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    if (!currentUserFeatures.filter(f => f.feature === "TutorReportsView")[0].enabled) {
      console.error("User does not have th required permissions to view the tutor reports screen");
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname } });
    }

    // Placement id comes from the route (/view-sessions/:id) so the screen is deep-linkable.
    this.placementId = Number(this.route.snapshot.paramMap.get('id'));
    this.tutorReportsHelperService.ensureContext(this.placementId);

    // lessonType is transient nav state; absent on an external link / refresh, so guard the read.
    const insertedState = this.router.getCurrentNavigation()?.extras?.state;
    if (insertedState != null) {
      this.lessonType = insertedState.lessonType as string;
    }

    if (!isNaN(this.placementId) && this.placementId > 0) {
      this.loadPlacementHeader();
      this.populateSessions();
    } else {
      this.router.navigate(["/"]);
    }
  }

  ngOnInit() {

  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public sessionSelected(selectedSession: PlacementSession): void {
    this.selectedSessionHolder = selectedSession;
    this.selectedSessionInfo = new SelectedSessionDate(selectedSession.startDate, selectedSession.startTime, selectedSession.endTime);

    this.router.navigate(['/add-session-v2', this.placementId, selectedSession.sessionId], {
      state: {
        placementId: this.placementId,
        selectedSessionDate: this.selectedSessionInfo,
        componentViewMode: 'Add'
      },
      queryParams: { returnUrl: window.location.pathname }
    });
  }

  /** Toggle a status in/out of the active filter (multi-select). */
  public toggleStatusFilter(status: string): void {
    const index = this.activeStatuses.indexOf(status);
    this.activeStatuses = index === -1
      ? [...this.activeStatuses, status]
      : this.activeStatuses.filter(s => s !== status);
  }

  public isStatusFilterActive(status: string): boolean {
    return this.activeStatuses.indexOf(status) !== -1;
  }

  /** Selecting "All" clears any status filter — the empty array is what the calendar reads as
   * "show everything", so "All" is simply the selected state whenever nothing specific is chosen. */
  public selectAllStatuses(): void {
    this.activeStatuses = [];
  }

  public isAllStatusesActive(): boolean {
    return this.activeStatuses.length === 0;
  }

  public showCalendarView(): void {
    this.panelNumber = 1;
    this.populateSessions();
  }

  public sessionDeleted(backToCalendar): void {
    if (backToCalendar) {
      this.showCalendarView();
    }
  }

  /** Feeds the utility-bar header from the shared placement context, fetching it on a deep link or refresh. */
  private loadPlacementHeader(): void {
    this.tutorReportsHelperService.placementData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => this.placementData = data);
    this.tutorReportsHelperService.placementMaintenance$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(maintenance => this.placementMaintenance = maintenance);

    this.tutorReportsHelperService.loadPlacementHeader(this.placementId, this.placementService,
      error => this.errorModalHelper.show(error.message, error));
  }

  private populateSessions(): void {
    let request = new SearchPlacementRequest();
    request.bookingId = this.placementId.toString();
    request.clientName = "";
    request.searchTerm = "";
    request.pendingSetupOnly = false;
    request.showEnded = true;
    request.clientIds = [];
    request.attachSessions = true;
    request.attachStudents = true;

    this.placementService.searchPlacements(request).subscribe((response: SearchPlacementResult[]) => {

      this.searchPlacementResult = response == null ? new SearchPlacementResult() : response[0];
      if (this.sessionCalendar) {
        this.sessionCalendar.populateSessionCalendar(this.searchPlacementResult);
      }
    }, error => {
      this.errorModalHelper.show(error.message, error);
    });
  }

}
